// src/cqcRollup.js — shared roll-up math, used by CqcCompliance.js (drill-down),
// CqcSites.js (overview cards) and CqcInspectorView.js, so none of the three
// ever disagree about a site's percentage. See cqcTaxonomy.js for what rolls
// into what, and for why "not applicable" is handled here rather than by
// filtering GROUPS itself.

export function attestationIndex(attestations) {
  const m = {};
  for (const a of attestations) m[`${a.siteId}|${a.groupId}|${a.itemId}|${a.elementId}`] = a;
  return m;
}

// applicabilityIndex/itemApplicableForSite — a quality statement is
// applicable to a site unless there's an explicit record saying otherwise
// (cqc_store.js's setApplicability; see cqcTaxonomy.js's header for why this
// is opt-out per site rather than inferred from service type). Every
// percentage function below takes the resulting map as an optional last
// `appByKey` argument — omit it and every function behaves exactly as
// before (every statement counted, useful for e.g. a single-sector org that
// has no need for the feature).
export function applicabilityIndex(records) {
  const m = {};
  for (const r of records || []) m[`${r.siteId}|${r.groupId}|${r.itemId}`] = r;
  return m;
}

export function itemApplicableForSite(appByKey, siteId, groupId, itemId) {
  if (!appByKey) return true;
  const r = appByKey[`${siteId}|${groupId}|${itemId}`];
  return !r || r.applicable !== false;
}

export function elementDone(attByKey, siteId, groupId, itemId, elementId) {
  return !!attByKey[`${siteId}|${groupId}|${itemId}|${elementId}`];
}

export function itemPctForSite(attByKey, siteId, groupId, item, appByKey) {
  if (!itemApplicableForSite(appByKey, siteId, groupId, item.id)) return null;
  const total = item.elements.length;
  if (!total) return null;
  const doneCount = item.elements.filter((el) => elementDone(attByKey, siteId, groupId, item.id, el.id)).length;
  return (doneCount / total) * 100;
}

export function groupPctForSite(attByKey, siteId, group, appByKey) {
  const vals = group.items.map((it) => itemPctForSite(attByKey, siteId, group.id, it, appByKey)).filter((v) => v != null);
  if (!vals.length) return null;
  return vals.reduce((a, b) => a + b, 0) / vals.length;
}

// A site's overall % across every key question — the number shown on a
// site's summary card. Statements marked not-applicable for this site drop
// out of the average entirely rather than counting as unmet.
export function overallPctForSite(attByKey, siteId, groups, appByKey) {
  let total = 0, done = 0;
  for (const group of groups) for (const item of group.items) {
    if (!itemApplicableForSite(appByKey, siteId, group.id, item.id)) continue;
    for (const element of item.elements) {
      total++;
      if (elementDone(attByKey, siteId, group.id, item.id, element.id)) done++;
    }
  }
  return total ? done / total * 100 : null;
}

export function itemPctAcrossSites(attByKey, sites, groupId, item, appByKey) {
  if (!sites.length) return null;
  const vals = sites.map((s) => itemPctForSite(attByKey, s._id, groupId, item, appByKey)).filter((v) => v != null);
  if (!vals.length) return null;
  return vals.reduce((a, b) => a + b, 0) / vals.length;
}

export function groupPctAcrossSites(attByKey, sites, group, appByKey) {
  const vals = group.items.map((it) => itemPctAcrossSites(attByKey, sites, group.id, it, appByKey)).filter((v) => v != null);
  if (!vals.length) return null;
  return vals.reduce((a, b) => a + b, 0) / vals.length;
}

// -----------------------------------------------------------------------------
// elementStatus — the single place that turns (applicability, assignment)
// into the status the Worklist (CqcWorklist.js) colour-codes and badges by.
//
// UPDATE 7: this status is now MANUALLY SET (a "not started / ongoing /
// completed" dropdown in the Worklist's detail panel — see
// assignment.status, set via POST /api/cqc/compliance/assignments), not
// derived from whether an attestation exists or a date has passed. That's a
// deliberate change from the previous auto-derived red/amber/green: a
// supervisor asked for direct control over what a row's status says rather
// than it being inferred, since "an evidence source is on file" and "this
// item is actually done" aren't always the same thing in practice.
//
//   completed    — assignment.status === "completed". Green.
//   ongoing      — assignment.status === "ongoing". Amber.
//   not-started  — assignment.status is "not-started", unset, or there's no
//                  assignment record at all yet (nothing has ever been
//                  touched on this row). Red.
//   not-applicable — excluded via cqc_applicability; the Worklist filters
//                  these out by default rather than colour-coding them.
//
// A proof item's own review date passing is surfaced separately, inline
// next to that item in the Worklist's Proof panel (see CqcWorklist.js) —
// it's informational there rather than silently overriding a status someone
// set on purpose.
// -----------------------------------------------------------------------------
export function elementStatus({ applicable = true, assignment } = {}) {
  if (applicable === false) return "not-applicable";
  const s = assignment && assignment.status;
  return s === "ongoing" || s === "completed" ? s : "not-started";
}

export function assignmentIndex(assignments) {
  const m = {};
  for (const a of assignments || []) m[`${a.siteId}|${a.groupId}|${a.itemId}|${a.elementId}`] = a;
  return m;
}

// evidenceItemIndex — UPDATE 7. Unlike the other *Index helpers here (one
// record per key), an element can have MANY evidence items, so this maps a
// key to an ARRAY, ordered oldest-first (matches listEvidenceItems' sort).
export function evidenceItemIndex(evidenceItems) {
  const m = {};
  for (const it of evidenceItems || []) {
    const key = `${it.siteId}|${it.groupId}|${it.itemId}|${it.elementId}`;
    (m[key] || (m[key] = [])).push(it);
  }
  return m;
}

// Completion is an explicit review decision; a source alone is setup, not completion.
export function completionIndex(assignments) {
  return assignmentIndex((assignments || []).filter(a => a.status === "completed"));
}
